import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";

import { completeLoginAuthNavigation } from "../components/auth/loginAuthNavigation";
import {
  resolveNextLoginRecheckFailure,
  seedLoginRecheckFailure,
  type LoginRecheckFailure,
} from "../components/auth/loginAuthRecheck.logic";
import { LoginAuthSessionRecheckCoordinator } from "../components/auth/LoginAuthSessionRecheckCoordinator";
import { PairingPendingSurface, PairingRouteSurface } from "../components/auth/PairingRouteSurface";
import { restoreDesktopRouteAfterAuth } from "../lib/desktopRoutePersistence";
import { resolveLoginRouteRedirect } from "../lib/desktopOnboarding";

export const Route = createFileRoute("/login")({
  beforeLoad: async ({ context }) => {
    const { authGateState } = context;
    const target = resolveLoginRouteRedirect(authGateState.status === "authenticated");
    if (target) {
      throw redirect({ to: target, replace: true });
    }
    return {
      authGateState,
    };
  },
  component: LoginRouteView,
  pendingComponent: LoginRoutePendingView,
});

function LoginRouteView() {
  const { authGateState } = Route.useRouteContext();
  const navigate = useNavigate();
  const router = useRouter();
  const gateErrorMessage =
    authGateState?.status === "requires-auth" ? authGateState.errorMessage : undefined;
  // Seeded from the gate error the surface is already showing, so the mount
  // recheck returning that same message is a no-op rather than a remount.
  const [recheckFailure, setRecheckFailure] = useState<LoginRecheckFailure | null>(() =>
    seedLoginRecheckFailure(gateErrorMessage),
  );

  const handleStillRequiresAuth = (errorMessage: string | undefined) => {
    setRecheckFailure((previous) => resolveNextLoginRecheckFailure(previous, errorMessage));
  };

  const handleAuthenticated = () => {
    void completeLoginAuthNavigation({
      invalidateRouter: () => router.invalidate(),
      restoreDesktopRoute: restoreDesktopRouteAfterAuth,
      navigateHome: () => {
        void navigate({ to: "/", replace: true });
      },
    });
  };

  if (!authGateState) {
    return null;
  }

  const errorMessage = recheckFailure?.message ?? gateErrorMessage;

  return (
    <>
      <LoginAuthSessionRecheckCoordinator
        onAuthenticated={handleAuthenticated}
        onStillRequiresAuth={handleStillRequiresAuth}
      />
      <PairingRouteSurface
        // The surface seeds its error text from `initialErrorMessage` once, so
        // a recheck failure only reaches the user by remounting. `attempt`
        // moves only when the message changes, so repeated identical failures
        // leave a part-filled form alone.
        key={`recheck-${recheckFailure?.attempt ?? 0}`}
        onAuthenticated={handleAuthenticated}
        {...(errorMessage ? { initialErrorMessage: errorMessage } : {})}
      />
    </>
  );
}

function LoginRoutePendingView() {
  return <PairingPendingSurface />;
}
